/**
 * queue/store.tsx
 * The SK Queue in-memory store as a React context. It owns the QueueState,
 * runs the 1-second live tick, manages the toast, and exposes action methods
 * that wrap the deterministic engine (engine.ts) plus the guest/walk-in/config
 * flows. This is the React equivalent of the prototype's Component class — same
 * state shape, same actions, same semantics.
 *
 * Actions clone the slices they touch, run the pure engine over the clones, set
 * state, and surface a toast. That keeps the engine pure and the component tree
 * re-rendering predictably.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { createInitialState } from './seed';
import {
  appendEvent, waitingFIFO, runAutoSeat, runPickResource, runSettle,
  runCallNext, runSeatCalled, runNoShow, type EngineResult,
} from './engine';
import type { QueueState, Entry, Channel, ToastMsg, Cfg1, Cfg2, GuestFlow } from './types';
import { DEV_TOOLS_ENABLED } from '../context/devFlags';
import { useDataSource } from './dataSource';
import { useActiveQueue } from './activeQueue';
import { getLive, getFloor, getQueue, seatEntry, releaseEntry, removeEntry, allocateNext, joinQueue } from '../services/queue/queueService';
import { liveLineToEntries, floorToResources } from './liveAdapter';
import { dtoToCfg1, dtoToCfg2 } from '../services/queue/queueMappers';
import { useQueueStream } from '../screens/hooks/useQueueStream';
import { handleApiError } from '../utils/errorHandler';

// Monotonic token sequence for newly-created entries (prototype starts at 48).
let SEQ = 48;
const nextToken = () => String(++SEQ);

interface QueueActions {
  // engine / host
  autoSeat: (entryId: string) => void;
  pickResource: (resId: string) => void;
  callNext: () => void;
  seatCalled: (entryId: string) => void;
  settle: (resId: string) => void;
  noShow: (entryId: string) => void;
  // selection
  selectedEntryId: string | null;
  selectEntry: (entryId: string) => void;
  clearSelection: () => void;
  // walk-in
  addWalkin: (w: { name: string; phone: string; party: number; channel: Channel }, seatNow: boolean) => void;
  // guest flow
  guestSet: (patch: Partial<GuestFlow>) => void;
  guestParty: (delta: number) => void;
  guestName: (v: string) => void;
  guestPhone: (v: string) => void;
  guestToggleConsent: () => void;
  guestJoin: () => boolean; // returns true if advanced to OTP
  guestVerify: (code?: string) => void;
  guestRemove: () => void;
  guestReset: () => void;
  // config
  setCfg1: <K extends keyof Cfg1>(key: K, val: Cfg1[K]) => void;
  setCfg2: <K extends keyof Cfg2>(key: K, val: Cfg2[K]) => void;
  toggleCfg2: (key: keyof Cfg2) => void;
  setMatch: (v: Cfg1['matchType']) => void;
  // toast
  toast: ToastMsg | null;
  showToast: (tag: string, msg: string) => void;
}

interface QueueContextValue extends QueueActions {
  state: QueueState;
  /** Active queue id when live (else null). For floor-mutating screens. */
  liveQueueId: string | null;
  /** Re-pull live (line + floor + cfg). No-op in memory mode. */
  refetchLive: () => Promise<void>;
}

const QueueContext = createContext<QueueContextValue | null>(null);

export function QueueProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<QueueState>(() => createInitialState(Date.now()));
  const [selectedEntryId, setSelectedEntryId] = useState<string | null>(null);
  const [toast, setToast] = useState<ToastMsg | null>(null);
  const toastTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // ── Live wiring (FITTED Host Console) ───────────────────────────────────
  // When the Host Console surface is wired live AND a queue is selected, the
  // store's line + floor come from the real read models and its seating actions
  // hit sk-queue-api (which delegates to SK Calendar via the backbone). In
  // memory mode this is all inert and the deterministic engine runs as before —
  // so every other surface and the demo are unaffected.
  const { isLiveSurface } = useDataSource();
  const { activeQueueId } = useActiveQueue();
  const liveSeating = isLiveSurface('host') && !!activeQueueId;
  const liveQueueId = liveSeating ? (activeQueueId as string) : null;

  /** Pull the live line + floor + config and project them into the store's
   *  slices. Config (cfg1/cfg2) matters because Host Console modals read it —
   *  e.g. the no-show/deposit ₹ amounts, shared-seating, maxParty — which would
   *  otherwise show the in-memory SEED defaults, not the active queue's real config. */
  const refetchLive = useCallback(async () => {
    if (!liveQueueId) return;
    try {
      const [live, floor, { queue }] = await Promise.all([
        getLive(liveQueueId), getFloor(liveQueueId), getQueue(liveQueueId),
      ]);
      const now = Date.now();
      const entries = liveLineToEntries(live, now);
      const resources = floorToResources(floor, entries);
      setState((s) => ({ ...s, entries, resources, now, queueName: queue.name, cfg1: dtoToCfg1(queue), cfg2: dtoToCfg2(queue) }));
    } catch (err) {
      handleApiError(err, 'Could not load the live floor');
    }
  }, [liveQueueId]);

  // Poll the live data while the Host Console is live (no WS in queue-api).
  // Also refetch immediately when the browser tab regains visibility, so a user
  // who flips to another tab (e.g. the Settings/guest preview), toggles a config
  // there, and comes back sees the change at once instead of waiting up to 4s —
  // this is what keeps the "Line paused" gate on Add walk-in current. (Web only;
  // document is undefined on native, where the 4s poll still covers it.)
  // SSE push: refetch the instant the queue moves. Poll below is the fallback.
  const { connected: streamConnected } = useQueueStream(liveQueueId ?? null, refetchLive);
  useEffect(() => {
    if (!liveQueueId) return;
    refetchLive();
    const t = setInterval(refetchLive, streamConnected ? 30000 : 4000);
    let onVis: (() => void) | undefined;
    if (typeof document !== 'undefined') {
      onVis = () => { if (document.visibilityState === 'visible') refetchLive(); };
      document.addEventListener('visibilitychange', onVis);
    }
    return () => {
      clearInterval(t);
      if (onVis && typeof document !== 'undefined') document.removeEventListener('visibilitychange', onVis);
    };
  }, [liveQueueId, refetchLive, streamConnected]);

  // ── 1-second live tick (advances clocks, timers, waits). ────────────────
  useEffect(() => {
    const id = setInterval(() => setState(s => ({ ...s, now: Date.now() })), 1000);
    return () => clearInterval(id);
  }, []);
  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const showToast = useCallback((tag: string, msg: string) => {
    setToast({ tag, msg });
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 3400);
  }, []);

  // Clone the mutable slices, run a mutation, commit, and toast its result.
  const apply = useCallback((fn: (entries: Entry[], resources: QueueState['resources']) => EngineResult) => {
    setState(prev => {
      const entries = prev.entries.map(e => ({ ...e, events: [...e.events] }));
      const resources = prev.resources.map(r => ({ ...r }));
      const res = fn(entries, resources);
      if (res.toastTag) showToast(res.toastTag, res.toastMsg);
      return { ...prev, entries, resources };
    });
  }, [showToast]);

  // Run a live write against sk-queue-api, then refetch + toast. Errors surface
  // via the shared handler and the refetch re-syncs to the server's truth.
  const liveWrite = useCallback(async (fn: () => Promise<unknown>, okTag: string, okMsg: string) => {
    try {
      await fn();
      await refetchLive();
      showToast(okTag, okMsg);
    } catch (err) {
      handleApiError(err, 'Action failed');
      await refetchLive();
    }
  }, [refetchLive, showToast]);

  // ── Engine / host actions ───────────────────────────────────────────────
  // Each action routes to sk-queue-api when the console is live, else runs the
  // in-memory engine. Seating delegates to SK Calendar server-side (FITTED).
  const autoSeat = useCallback((entryId: string) => {
    if (liveSeating) {
      void liveWrite(() => seatEntry(entryId, { mode: 'auto' }), 'SEATED', 'Seated to the best-fit table');
      setSelectedEntryId(null);
      return;
    }
    apply((entries, resources) => runAutoSeat(entries, resources, entryId, Date.now()));
    setSelectedEntryId(null);
  }, [apply, liveSeating, liveWrite]);

  const selectEntry = useCallback((entryId: string) => {
    setSelectedEntryId(cur => (cur === entryId ? null : entryId));
  }, []);
  const clearSelection = useCallback(() => setSelectedEntryId(null), []);

  const pickResource = useCallback((resId: string) => {
    if (!selectedEntryId) {
      // Tapping an occupied table with nothing selected → settle it.
      const r = state.resources.find(x => x.id === resId);
      if (r && r.status === 'occupied') { settleRef.current(resId); return; }
      showToast('PICK', 'Select a waiting party first, then tap a free table');
      return;
    }
    if (liveSeating) {
      // Pick mode: seat the selected entry onto the chosen table (cal calendar).
      const entryId = selectedEntryId;
      void liveWrite(() => seatEntry(entryId, { mode: 'pick', resourceRef: resId }), 'SEATED', 'Seated to the chosen table');
      setSelectedEntryId(null);
      return;
    }
    apply((entries, resources) => runPickResource(entries, resources, selectedEntryId, resId, state.cfg2, Date.now()));
    setSelectedEntryId(null);
  }, [apply, selectedEntryId, state.resources, state.cfg2, showToast, liveSeating, liveWrite]);

  const settle = useCallback((resId: string) => {
    if (liveSeating) {
      // Settle = release the entry occupying this table → frees it in cal.
      // The seated party is NOT in state.entries (that's the waiting line only);
      // its id lives on the resource (floor read model), so read it from there.
      const res = state.resources.find((r) => r.id === resId);
      const occId = res?.occupant ?? null;
      if (!occId) { showToast('SETTLE', 'No occupant to settle on that table'); return; }
      void liveWrite(() => releaseEntry(occId), 'SETTLED', 'Table settled & freed');
      return;
    }
    const orderNo = Math.floor(1000 + Math.random() * 9000); // demo order id only
    apply((entries, resources) => runSettle(entries, resources, resId, orderNo, Date.now()));
  }, [apply, liveSeating, liveWrite, state.entries, state.resources, showToast]);
  // Ref so pickResource can call settle without a dependency cycle.
  const settleRef = useRef(settle);
  settleRef.current = settle;

  const callNext = useCallback(() => {
    if (liveSeating) {
      void liveWrite(() => allocateNext(liveQueueId as string), 'CALL NEXT', 'Allocated the front of the line');
      return;
    }
    apply((entries, resources) => runCallNext(entries, resources, Date.now()));
  }, [apply, liveSeating, liveWrite, liveQueueId]);

  const seatCalled = useCallback((entryId: string) => {
    if (liveSeating) {
      void liveWrite(() => seatEntry(entryId, { mode: 'auto' }), 'SEATED', 'Seated the called party');
      return;
    }
    apply((entries, resources) => runSeatCalled(entries, resources, entryId, Date.now()));
  }, [apply, liveSeating, liveWrite]);

  const noShow = useCallback((entryId: string) => {
    if (liveSeating) {
      void liveWrite(() => removeEntry(entryId, { reasonCode: 'no-show' }), 'NO-SHOW', 'Marked as no-show');
      setSelectedEntryId(null);
      return;
    }
    apply((entries, resources) => runNoShow(entries, resources, entryId, state.cfg2, Date.now()));
    setSelectedEntryId(null);
  }, [apply, state.cfg2, liveSeating, liveWrite]);

  // ── Walk-in ───────────────────────────────────────────────────────────────
  const addWalkin = useCallback((w: { name: string; phone: string; party: number; channel: Channel }, seatNow: boolean) => {
    if (liveSeating) {
      // Staff walk-in via STAFF channel (no DPDP self-join consent gate).
      // seatNow → autoAllocate:true so the server zero-wait seats on join.
      // "Add to line" → autoAllocate:false so the party PARKS as WAITING even
      // when a table is free (the host seats them later via Call next / Pick).
      // Idempotency key makes a double-tap safe.
      const idem = `walkin-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      void liveWrite(async () => {
        await joinQueue(liveQueueId as string, {
          displayName: w.name.trim() || undefined,
          phoneE164: w.phone ? `+91${w.phone.replace(/\D/g, '')}` : undefined,
          partySize: w.party,
          joinChannel: 'STAFF',
          idempotencyKey: idem,
          autoAllocate: seatNow,
        });
      }, 'JOINED', seatNow ? 'Walk-in joined & seated' : 'Walk-in added to the line');
      return;
    }
    setState(prev => {
      const entries = prev.entries.map(e => ({ ...e, events: [...e.events] }));
      const resources = prev.resources.map(r => ({ ...r }));
      const now = Date.now();
      const e: Entry = {
        id: 'e_w' + now, token: nextToken(), displayName: w.name.trim() || 'Walk-in',
        phone: w.phone, party: w.party, channel: w.channel, state: 'WAITING',
        joinAt: now, allocatedResource: null, consent: true, verified: true, events: [],
      };
      appendEvent(e, 'JOINED', 'STAFF', now);
      appendEvent(e, 'VERIFIED', 'STAFF', now, 'staff-entry');
      appendEvent(e, 'WAITING', 'SYSTEM', now);
      entries.push(e);
      if (seatNow) {
        const res = runAutoSeat(entries, resources, e.id, now);
        showToast(res.ok ? 'ZERO-WAIT' : 'HOLD',
          res.ok ? `${e.displayName} joined & seated` : `${e.displayName} joined — no fit free, holding`);
      } else {
        showToast('JOINED', `${e.displayName} added to the line (#${e.token})`);
      }
      return { ...prev, entries, resources };
    });
  }, [showToast, liveSeating, liveWrite, liveQueueId]);

  // ── Guest flow ────────────────────────────────────────────────────────────
  const guestSet = useCallback((patch: Partial<GuestFlow>) => {
    setState(s => ({ ...s, guest: { ...s.guest, ...patch } }));
  }, []);
  const guestParty = useCallback((delta: number) => {
    setState(s => {
      const max = s.cfg2.maxParty || 12;
      const p = Math.max(1, Math.min(max, s.guest.party + delta));
      return { ...s, guest: { ...s.guest, party: p } };
    });
  }, []);
  const guestName = useCallback((v: string) => {
    guestSet({ name: (v || '').slice(0, 160) });
  }, [guestSet]);
  const guestPhone = useCallback((v: string) => {
    // 10 digits, digits-only — same as the platform signup field.
    guestSet({ phone: (v || '').replace(/\D/g, '').slice(0, 10) });
  }, [guestSet]);
  const guestToggleConsent = useCallback(() => {
    setState(s => ({ ...s, guest: { ...s.guest, consent: !s.guest.consent } }));
  }, []);
  const guestJoin = useCallback((): boolean => {
    const g = state.guest;
    const digits = g.phone.replace(/\D/g, '');
    if (!g.name.trim()) { showToast('CHECK', 'Please enter your name'); return false; }
    if (digits.length !== 10) { showToast('CHECK', 'Enter a valid 10-digit mobile number'); return false; }
    if (!g.consent) { showToast('CONSENT', 'Please accept the notice to continue'); return false; }
    guestSet({ step: 'otp', otp: '' });
    return true;
  }, [state.guest, showToast, guestSet]);
  const guestVerify = useCallback((codeArg?: string) => {
    setState(prev => {
      const code = (codeArg ?? prev.guest.otp).replace(/\D/g, '');
      if (code.length < 6) { showToast('OTP', 'Enter the 6-digit code'); return prev; }
      const entries = prev.entries.map(e => ({ ...e, events: [...e.events] }));
      const now = Date.now();
      const e: Entry = {
        id: 'e_you' + now, token: nextToken(), displayName: prev.guest.name.trim() || 'You',
        phone: prev.guest.phone.replace(/\D/g, ''), party: prev.guest.party,
        channel: 'QR', state: 'WAITING', joinAt: now, allocatedResource: null,
        consent: true, verified: true, events: [],
      };
      appendEvent(e, 'JOINED', 'GUEST', now);
      appendEvent(e, 'VERIFIED', 'SYSTEM', now, 'otp');
      appendEvent(e, 'WAITING', 'SYSTEM', now);
      entries.push(e);
      showToast('JOINED', `You joined the waitlist (#${e.token})`);
      return { ...prev, entries, guest: { ...prev.guest, step: 'status', entryId: e.id } };
    });
  }, [showToast]);
  const guestRemove = useCallback(() => {
    setState(prev => {
      const entries = prev.entries.map(e => ({ ...e, events: [...e.events] }));
      const e = entries.find(x => x.id === prev.guest.entryId);
      if (e) appendEvent(e, 'REMOVED', 'GUEST', Date.now(), 'self-removal');
      return { ...prev, entries, guest: { ...prev.guest, step: 'removed' } };
    });
  }, []);
  const guestReset = useCallback(() => {
    guestSet({ step: 'join', party: 2, name: '', phone: '', consent: false, otp: '', entryId: null });
  }, [guestSet]);

  // ── Config ────────────────────────────────────────────────────────────────
  const setCfg1 = useCallback(<K extends keyof Cfg1>(key: K, val: Cfg1[K]) => {
    setState(s => ({ ...s, cfg1: { ...s.cfg1, [key]: val } }));
  }, []);
  const setCfg2 = useCallback(<K extends keyof Cfg2>(key: K, val: Cfg2[K]) => {
    setState(s => ({ ...s, cfg2: { ...s.cfg2, [key]: val } }));
  }, []);
  const toggleCfg2 = useCallback((key: keyof Cfg2) => {
    setState(s => ({ ...s, cfg2: { ...s.cfg2, [key]: !s.cfg2[key] } }));
  }, []);
  const setMatch = useCallback((v: Cfg1['matchType']) => {
    setState(s => ({ ...s, cfg1: { ...s.cfg1, matchType: v } }));
  }, []);

  const value: QueueContextValue = {
    state,
    autoSeat, pickResource, callNext, seatCalled, settle, noShow,
    selectedEntryId, selectEntry, clearSelection,
    addWalkin,
    guestSet, guestParty, guestName, guestPhone, guestToggleConsent, guestJoin, guestVerify, guestRemove, guestReset,
    setCfg1, setCfg2, toggleCfg2, setMatch,
    toast, showToast,
    // Live-mode handles for screens that mutate the floor (add/delete table) and
    // want an immediate refresh rather than waiting for the 4s poll.
    liveQueueId, refetchLive,
  };

  // DEV-only: expose the live store on window for debugging + headless QA
  // (driving flows that are awkward to reach via synthetic pointer events on
  // RN-Web touchables). Never present in production builds.
  if (DEV_TOOLS_ENABLED && typeof window !== 'undefined') {
    (window as any).__skq = value;
  }

  return <QueueContext.Provider value={value}>{children}</QueueContext.Provider>;
}

export function useQueue(): QueueContextValue {
  const ctx = useContext(QueueContext);
  if (!ctx) throw new Error('useQueue must be used within QueueProvider');
  return ctx;
}

// Convenience selectors built on the store (re-derive on each render).
export function useWaitingFIFO(): Entry[] {
  const { state } = useQueue();
  return waitingFIFO(state.entries);
}
